import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, MessageCircle, Sparkles, Info, Ban } from "lucide-react";
import { api } from "@/lib/api";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

export default function CostsModal({ open, course, onClose, onPickBranch }) {
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const prices = course?.prices || {};
  const numericPrices = Object.values(prices).filter((v) => typeof v === "number");
  const minPrice = numericPrices.length ? Math.min(...numericPrices) : null;
  const maxPrice = numericPrices.length ? Math.max(...numericPrices) : null;

  // Ranking: numeric first (ascending), then "consultar", then "no_disponible"
  const rank = (p) => {
    if (typeof p === "number") return 0;
    if (p === "consultar") return 1;
    return 2;
  };

  const rows = useMemo(
    () =>
      branches
        .map((b) => ({ ...b, price: prices[b.id] }))
        .filter((b) => b.price !== undefined && b.price !== null)
        .sort((a, b) => {
          const r = rank(a.price) - rank(b.price);
          if (r !== 0) return r;
          if (typeof a.price === "number" && typeof b.price === "number") {
            return a.price - b.price;
          }
          return 0;
        }),
    [branches, prices]
  );

  return (
    <AnimatePresence>
      {open && course && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-end md:items-center justify-center"
          data-testid="costs-modal-overlay"
        >
          <button
            aria-label="Cerrar"
            data-testid="costs-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="costs-modal-title"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white w-full md:w-[94%] md:max-w-4xl max-h-[92dvh] md:max-h-[92vh] flex flex-col overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.45)]"
            data-testid="costs-modal"
          >
            <div className="overflow-y-auto flex-1 relative">
            <div className="sticky top-0 z-30 flex justify-end pointer-events-none">
              <button
                onClick={onClose}
                aria-label="Cerrar"
                data-testid="costs-modal-close"
                className="pointer-events-auto m-2 md:m-3 w-10 h-10 flex items-center justify-center bg-white text-[#0a0a0a] border border-black/10 hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors shadow-md"
              >
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>
            {/* Header */}
            <div className="-mt-12 px-7 pt-2 pb-7 md:px-10 md:pt-4 md:pb-10 lg:px-12 lg:pb-12 border-b border-black/8">
              <div className="flex items-center gap-3 mb-4">
                <span className="dot-red" />
                <span className="overline">Inversión · Pago semanal</span>
              </div>
              <h3
                id="costs-modal-title"
                data-testid="costs-modal-title"
                className="font-display font-semibold tracking-tighter text-3xl md:text-4xl text-[#0a0a0a] leading-tight"
              >
                Costos de {course.title}
              </h3>
              <p className="mt-3 text-sm md:text-base font-light text-[#525252] max-w-2xl leading-relaxed">
                Precio semanal por sucursal. Sin enganche · Plan de pagos
                flexible · Material profesional incluido.
              </p>

              {minPrice !== null ? (
                <div className="mt-6 inline-flex flex-wrap items-baseline gap-2 bg-[#fafafa] border border-black/8 px-5 py-3">
                  <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#525252] font-semibold">
                    Desde
                  </span>
                  <span
                    data-testid="costs-modal-from"
                    className="font-display font-bold text-3xl text-[#c40000]"
                  >
                    {formatMXN(minPrice)}
                  </span>
                  <span className="text-xs text-[#525252]">/ semana</span>
                  {maxPrice && maxPrice !== minPrice && (
                    <span className="text-xs text-[#525252] ml-2">
                      · hasta {formatMXN(maxPrice)} / semana
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-6 inline-flex items-center gap-2 bg-[#fafafa] border border-black/8 px-5 py-3">
                  <Info size={14} strokeWidth={1.6} className="text-[#c40000]" />
                  <span className="text-sm font-semibold text-[#0a0a0a]">
                    Consulta el costo y la disponibilidad con la sucursal de tu preferencia.
                  </span>
                </div>
              )}
            </div>

            {/* List of branches */}
            <div className="p-7 md:p-10 lg:p-12">
              <div className="space-y-3">
                {rows.map((b, i) => {
                  const isNumber = typeof b.price === "number";
                  const isConsult = b.price === "consultar";
                  const isUnavailable = b.price === "no_disponible";
                  return (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: i * 0.04 }}
                      className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 border p-5 transition-colors ${
                        isUnavailable
                          ? "border-black/8 bg-neutral-50/70 opacity-70"
                          : "border-black/8 hover:border-[#c40000]"
                      }`}
                      data-testid={`costs-row-${b.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-lg tracking-tight text-[#0a0a0a]">
                          {b.name}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-[#525252]">
                          <MapPin size={12} strokeWidth={1.5} className="shrink-0" />
                          <span className="truncate">{b.address}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between md:justify-end gap-4">
                        <div className="text-right">
                          {isNumber && (
                            <>
                              <div
                                className="font-display font-bold text-2xl md:text-[1.65rem] text-[#0a0a0a] leading-none"
                                data-testid={`costs-price-${b.id}`}
                              >
                                {formatMXN(b.price)}
                              </div>
                              <div className="mt-1 text-[0.65rem] uppercase tracking-[0.18em] text-[#525252]">
                                MXN / semana
                              </div>
                            </>
                          )}
                          {isConsult && (
                            <span
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a0a0a] text-white text-[0.7rem] font-semibold uppercase tracking-[0.15em]"
                              data-testid={`costs-price-${b.id}`}
                            >
                              <Info size={12} strokeWidth={1.8} />
                              Consultar precio
                            </span>
                          )}
                          {isUnavailable && (
                            <span
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-200 text-[#525252] text-[0.7rem] font-semibold uppercase tracking-[0.15em]"
                              data-testid={`costs-price-${b.id}`}
                            >
                              <Ban size={12} strokeWidth={1.8} />
                              No disponible
                            </span>
                          )}
                        </div>
                        {!isUnavailable && (
                          <a
                            href={`https://wa.me/${b.whatsapp}?text=${encodeURIComponent(
                              isNumber
                                ? `Hola ${b.name}, quiero inscribirme al curso de ${course.title}. Vi el costo de $${b.price}/semana.`
                                : `Hola ${b.name}, quiero información y costos del curso de ${course.title}.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`costs-wa-${b.id}`}
                            className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25d366] text-white text-[0.7rem] font-semibold uppercase tracking-[0.15em] hover:bg-[#1ebe5d] transition-colors"
                          >
                            <MessageCircle size={14} strokeWidth={1.8} />
                            {isNumber ? "Apartar" : "Consultar"}
                          </a>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  data-testid="costs-modal-enroll"
                  onClick={() => {
                    onClose();
                    if (onPickBranch) onPickBranch();
                  }}
                  className="btn-primary"
                >
                  Inscribirme a este curso
                </button>
              </div>
            </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
